/** Shared helpers for category colors (tokens or #RRGGBB hex). */

export const TOKEN_TO_HEX = {
  blue: '#124191',
  primary: '#ef4444',
  amber: '#f59e0b',
  green: '#10b981',
  yellow: '#eab308',
  rose: '#f43f5e',
};

export const DEFAULT_CATEGORY_HEX = '#124191';

/** Curated palette shown in the admin color picker. */
export const CATEGORY_COLOR_PALETTE = [
  '#124191',
  '#0045F5',
  '#1a56b8',
  '#3b78d1',
  '#ef4444',
  '#dc2626',
  '#f43f5e',
  '#e11d48',
  '#f59e0b',
  '#d97706',
  '#eab308',
  '#ca8a04',
  '#10b981',
  '#059669',
  '#00C957',
  '#14b8a6',
  '#8b5cf6',
  '#7c3aed',
  '#6366f1',
  '#0ea5e9',
  '#64748b',
  '#334155',
  '#0f172a',
  '#ffffff',
];

const HEX_RE = /^#([0-9A-Fa-f]{6})$/;
const SHORT_HEX_RE = /^#([0-9A-Fa-f]{3})$/;

export function isHexColor(value) {
  const v = String(value || '').trim();
  return HEX_RE.test(v) || SHORT_HEX_RE.test(v);
}

export function normalizeHex(value, fallback = DEFAULT_CATEGORY_HEX) {
  const raw = String(value || '').trim();
  if (TOKEN_TO_HEX[raw]) return TOKEN_TO_HEX[raw];
  if (HEX_RE.test(raw)) return raw.toUpperCase();
  const short = raw.match(SHORT_HEX_RE);
  if (short) {
    const [r, g, b] = short[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return fallback;
}

export function resolveCategoryHex(value, fallback = DEFAULT_CATEGORY_HEX) {
  return normalizeHex(value, fallback);
}

export function hexToRgb(hex) {
  const normalized = normalizeHex(hex);
  const n = parseInt(normalized.slice(1), 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

export function rgbToHex(r, g, b) {
  const clamp = (v) => Math.max(0, Math.min(255, Number(v) || 0));
  const to2 = (v) => clamp(v).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`.toUpperCase();
}

export function hexToRgba(hex, alpha = 1) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Inline CSS variables for calendar chips / accents. */
export function eventColorCssVars(color) {
  const hex = resolveCategoryHex(color);
  return {
    '--event-bg': hexToRgba(hex, 0.14),
    '--event-border': hexToRgba(hex, 0.35),
    '--event-accent': hex,
    '--event-fg': hex,
  };
}

/**
 * Returns className + style for calendar event coloring.
 * Legacy token classes kept when value is a known token (no #).
 */
export function eventColorClassAndStyle(color) {
  const raw = String(color || '').trim();
  if (raw && !raw.startsWith('#') && TOKEN_TO_HEX[raw]) {
    return { className: `event-${raw}`, style: {} };
  }
  return {
    className: 'event-custom',
    style: eventColorCssVars(raw),
  };
}
